import { t } from '../i18n/strings'

export function canShareText(): boolean {
  return typeof navigator.share === 'function'
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to execCommand
  }

  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch {
    return false
  }
}


/** Prefer Web Share; clipboard only when Share API is unavailable.
 * Soft residual 6: after an OS share attempt, do not clipboard-fallback —
 * cancel/failure must not look like in-app Copy success (OS sheets vary).
 */
export async function shareOrCopyText(
  text: string,
): Promise<'shared' | 'copied' | 'aborted' | 'failed'> {
  if (canShareText()) {
    try {
      await navigator.share({
        title: t().brand,
        text,
      })
      return 'shared'
    } catch (err) {
      const name = err instanceof Error ? err.name : ''
      if (name === 'AbortError') return 'aborted'
      return 'failed'
    }
  }

  const ok = await copyTextToClipboard(text)
  return ok ? 'copied' : 'failed'
}

export function canShareFiles(): boolean {
  try {
    if (typeof navigator === "undefined" || typeof navigator.share !== "function") return false
    if (typeof navigator.canShare !== "function") return false
    const probe = new File([new Blob(["x"], { type: "image/png" })], "t.png", { type: "image/png" })
    return navigator.canShare({ files: [probe] })
  } catch {
    return false
  }
}

export async function downloadPngBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    a.rel = "noopener"
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 2000)
  }
}

export async function sharePngFile(
  blob: Blob,
  filename: string,
  title: string,
): Promise<"shared" | "aborted" | "unsupported" | "failed"> {
  if (!canShareFiles()) return "unsupported"
  const file = new File([blob], filename, { type: "image/png" })
  try {
    await navigator.share({ files: [file], title })
    return "shared"
  } catch (err) {
    const name = err instanceof Error ? err.name : ""
    if (name === "AbortError") return "aborted"
    return "failed"
  }
}
